import React, { useState, useRef, useCallback, useEffect, Fragment } from 'react';
import {
    GoogleMap,
    Polygon,
    MarkerF,
    Circle,
} from '@react-google-maps/api';
import {
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import _, { delay } from 'lodash';
import { useSelector } from 'react-redux';
import { When } from 'react-if';
import { Delete, Edit } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useImmer } from 'use-immer';
import produce from 'immer';
import { RootState } from '../../../store';
import { GeofenceService } from '../../../services/geofence.service';
import { GeofenceTypes } from '../../../types/geofence.types';
import { GeofenceForm } from '../../forms/geofence';
import AreYouSureModal from '../../are-you-sure-modal';
import { GeofencePhoneForm } from '../../forms/geofencePhone';
import NoData from '../../no-data';
import { geoPhoneTypeLabel } from '../../../constants/geofence.constants';
import { EventMapButtonBar, defaultCoordinates } from '../event-map-button-bar/eventMapButtonBar';
import { useGenericQuery } from '../../../services/react-query/generic.query';
import { AlertTypes, DeviceTypes, EventTypes, ReportTypes } from '../../../types';
import { EventFactory } from '../../../factory/event.factory';
import { markerConstants, markerRadiusColorConstants } from '../../../constants/marker.constants';
import { LoadingButton } from '@mui/lab';
import { ZOOM_LEVEL_EVENT } from '..';
import { CustomMapPopup } from '../../custom-map-popup';
import { ReportDetailsDrawer } from '../../details-drawers/report-details-drawer';
import { DeviceDetailsDrawer } from '../../details-drawers/device-details-drawer';
import { AlertDetailsDrawer } from '../../details-drawers/alert-details-drawer';
import { MapLegend } from '../map-legend';
import {groupEvents,getMarkerType,containsMultipleEvents} from "../../../helpers/eventMapHelper";

type LatLng = {
    lat: number;
    lng: number;
};


// TRIANGLE_OVER_BERLIN are the coordinates of a triangle drawn over Berlin, in Germany. This is the shape of the starting polygon.
const TRIANGLE_OVER_BERLIN = [
    { lat: 52.52549080781086, lng: 13.398118538856465 },
    { lat: 52.48578559055679, lng: 13.36653284549709 },
    { lat: 52.48871246221608, lng: 13.44618372440334 },
];

// COLUMBIA_UNIVERSITY are the coordinates of Columbia University in New York City
const COLUMBIA_UNIVERSITY = { lat: 40.8075, lng: -73.9626 };

// computeCentroid yields the center point of a polygon
function computeCentroid(vertices: LatLng[]): LatLng {
    const sum = vertices.reduce(
        (acc, val) => {
            acc.lat += val.lat;
            acc.lng += val.lng;
            return acc;
        },
        { lat: 0, lng: 0 }
    );

    return {
        lat: sum.lat / vertices.length,
        lng: sum.lng / vertices.length,
    };
}
// originalCentroid is the center of points
const originalCentroid: LatLng = computeCentroid(TRIANGLE_OVER_BERLIN);

// differences are the displacements in lat and lng from a central point to draw a triangle
const differences = TRIANGLE_OVER_BERLIN.map((point) => ({
    lat: point.lat - originalCentroid.lat,
    lng: point.lng - originalCentroid.lng,
}));

// generateTriangle takes a point and a zoomLevel scaling factor to draw a triangle around a point
function generateTriangle(coordinate: LatLng, zoomLevel: number): LatLng[] {
    const SCALE = ((24 - zoomLevel) / (24 - 4) + 1) * 0.3;
    return differences.map((diff) => ({
        lat: coordinate.lat + diff.lat * SCALE,
        lng: coordinate.lng + diff.lng * SCALE,
    }));
}

export function DrawOnMap() {
    const isMountedRef = useRef<boolean>(false);
    const { data: unparsedEvents } = useGenericQuery<EventTypes.EventMapsItems>(
        'fetchEventMapItems',
        'eventMap/fetchDisplayEvents',
        2500,
    );
    const [popoverPosition, setPopoverPosition] = useState<LatLng | null>(null);

    const [eventModalVisible, setEventModalVisible] = useState(false);
    const [events, setEvents] = useState<EventTypes.Model[]>([]);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [zoom, setZoom] = useState<number>(defaultCoordinates.zoom);
    const [selectedEventIdx, setSelectedEventIdx] = useState<number | null>(null);
    const [optionsAnchor, setOptionsAnchor] = useState<null | HTMLElement>(
        null
    );
    const { filter } = useSelector((state: RootState) => state.eventMap);
    const INITIAL_ZOOM_LEVEL = 12;
    const INITIAL_CENTER = COLUMBIA_UNIVERSITY;
    const [selectedGeofence, setSelectedGeofence] = useState<null | GeofenceTypes.Model>(null);
    const [geofenceToUpdate, setGeofenceToUpdate] = useState<null | GeofenceTypes.UpdateGeofenceParams>(null);
    const [selectedPhone, setSelectedPhone] = useState<null | GeofenceTypes.GeofencePhoneNumber>(null);
    const { geofences, loading, geoPhones } = useSelector((state: RootState) => state.geofence);
    const [geofencesClone, setGeofencesClone] = useImmer(geofences);
    // define state
    const [zoomLevel, setZoomLevel] = useState(INITIAL_ZOOM_LEVEL);
    const [center, setCenter] = useState<LatLng>(INITIAL_CENTER);

    const INITIAL_PATH_COORDINATES = generateTriangle(center, zoomLevel);

    const alreadyHaveOutsideLocation = geoPhones.filter(geo => geo.type === 'outside')?.length > 0;
    const alreadyHaveUnknownLocation = geoPhones.filter(geo => geo.type === 'unknown')?.length > 0;

    // Define refs for Polygon instanc, listeners, and ref
    const polygonRefs = useRef<(google.maps.Polygon | null)[]>([]);
    const listenersRef = useRef<google.maps.MapsEventListener[][]>([]);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    //changes for group Events
    const [isMultipleEvents, setIsMultipleEvents] = useState(false);

    const [groupByEvents, setGroupByEvents] = useState<any>({});
    
    // Hover tracking for popup auto-close
    const [isHoveringPopup, setIsHoveringPopup] = useState(false);
    const popupCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    let groupByKey : 'latLngString' | 'latLngStringApprox' = 'latLngStringApprox';
    if(zoom >= 16)
        groupByKey = 'latLngString';

    const [modal, setModal] = useState({
        phoneForm: false,
        form: false,
        delete: false,
        confirmEdit: false,
        deletePhone: false,
    });

    const toggleModal = (id: keyof typeof modal, v: boolean) => setModal(m => ({ ...m, [id]: v }));

    // onEdit of one of the polygons in the array, by index
    const onEdit = useCallback(
        async (index: number, geofenceId?: string) => {
            const polygon = polygonRefs.current[index];
            const geofenceIdx = geofencesClone?.findIndex(geo => geo.id === geofenceId);

            const geofence = geofencesClone[geofenceIdx];

            if (geofence) {
                const newPath = polygon
                    ?.getPath()
                    .getArray()
                    .map((coordinate) => ({
                        lat: coordinate.lat(),
                        lng: coordinate.lng(),
                    }));

                const coordinates = newPath?.map((coordinate) => ({
                    latitude: coordinate.lat,
                    longitude: coordinate.lng,
                })) as GeofenceTypes.GeofenceCoordinate[];

                setGeofencesClone(produce((geo) => {
                    geo[geofenceIdx].coordinates = coordinates;
                }));
            }
        },
        [geofencesClone]
    );

    const handleLoad =
        // Bind refs to current Polygon and listeners
        useCallback(
            (polygon: google.maps.Polygon, index: number) => {
                if (!isMountedRef.current) {
                    return;
                }
                // add polygon to array
                polygonRefs.current[index] = polygon;
                const path = polygon.getPath();

                // ensure ListenersRef exists
                if (!listenersRef.current[index]) {
                    listenersRef.current[index] = [];
                }

                listenersRef.current[index].push(
                    path.addListener('set_at', () => onEdit(index)),
                    path.addListener('insert_at', () => onEdit(index)),
                    path.addListener('remove_at', () => onEdit(index))
                );
            },
            [onEdit]
        );

    const handleUnmount = useCallback((index: number) => {
        listenersRef.current[index].forEach((lis) => lis.remove());
        polygonRefs.current[index] = null;
    }, []);

    const handleMapLoad = (map: google.maps.Map) => {
        mapRef.current = map;
        map.controls[google.maps.ControlPosition.LEFT_TOP].push(
            document.getElementById('legend')!);
    };

    const updateBulkGeofences = async () => {
        const params = geofencesClone?.map((g) => ({ id: g.id, coordinates: g.coordinates })) as GeofenceTypes.BulkGeofenceUpdateParams;
        await GeofenceService.bulkGeofenceUpdate(params, (isValid, msg) => {
            if (!isValid) {
                toast(
                    msg,
                    {
                        autoClose: 1500,
                        type: 'error',
                    }
                );
                return;
            }

            toast(
                'Changes on geofences applied!',
                {
                    autoClose: 1500,
                    type: 'success',
                }
            );
        });
    }

    const handleOptionsClose = () => {
        setOptionsAnchor(null);
    };

    const handleClick = (event: any) => {
        setAnchorEl(event.target);
    };

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (unparsedEvents) {
            const evts = EventFactory.transformAlertsAndDevices(
                unparsedEvents.alerts,
                unparsedEvents.devices,
                unparsedEvents.reports,
                unparsedEvents.users,
                filter,
            );

            setGroupByEvents(groupEvents(evts,groupByKey));
            setEvents(evts);
        }
    }, [unparsedEvents, filter]);

    useEffect(() => {
        if(containsMultipleEvents(groupByEvents))
            setIsMultipleEvents(true);
    }, [groupByEvents]);

    useEffect(() => {
        if (geofences?.length > 0) {
            setGeofencesClone(geofences);
        }
    }, [geofences]);

    // Auto-close popup when mouse leaves the popup card
    useEffect(() => {
        // Clear any existing timeout
        if (popupCloseTimeoutRef.current) {
            clearTimeout(popupCloseTimeoutRef.current);
            popupCloseTimeoutRef.current = null;
        }

        // If popup is visible but user is NOT hovering over it, start close timer
        if (!isHoveringPopup && selectedEventIdx !== null) {
            popupCloseTimeoutRef.current = setTimeout(() => {
                setSelectedEventIdx(null);
                setIsHoveringPopup(false);
            }, 500); // 500ms buffer
        }

        return () => {
            if (popupCloseTimeoutRef.current) {
                clearTimeout(popupCloseTimeoutRef.current);
            }
        };
    }, [isHoveringPopup, selectedEventIdx]);

    const geofencesChanged = !_.isEqual(geofences, geofencesClone);
    const selectedEvent = typeof selectedEventIdx === 'number' ? events[selectedEventIdx] : null;

    return (
        <>
            {modal.phoneForm &&
                <GeofencePhoneForm
                    visible={modal.phoneForm}
                    editGeofencePhone={selectedPhone}
                    onClose={() => {
                        setSelectedPhone(null);
                        toggleModal('phoneForm', false);
                    }}
                />
            }

            {modal.form &&
                <GeofenceForm
                    defaultCoordinates={INITIAL_PATH_COORDINATES?.map(({ lat, lng }) => ({ latitude: lat, longitude: lng }))}
                    visible={modal.form}
                    editGeofence={selectedGeofence}
                    onClose={() => {
                        setSelectedGeofence(null);
                        toggleModal('form', false);
                    }}
                />
            }

            <Grid sx={{ mt: -2 }}>
                <EventMapButtonBar setCenter={setCenter} setZoom={setZoomLevel} />
            </Grid>

            <When condition={geofencesChanged}>
                <Stack flexDirection={'row-reverse'}>
                    <LoadingButton loading={loading.updateGeofence} variant='contained' size='small' onClick={updateBulkGeofences}>
                        Save geofences changes
                    </LoadingButton>
                </Stack>
            </When>

                <Box sx={{ height: '80vh', width: '100%', mt: 2, position: 'relative' }}>
                    <When condition={loading.fetchGeofences}>
                        <GoogleMap
                            options={{
                                disableDefaultUI: true,
                                zoomControl: true,
                                fullscreenControl: true,
                                mapTypeControl: true,
                                streetViewControl: true,
                                scaleControl: true,
                                rotateControl: true,
                                clickableIcons: false,
                                scrollwheel: true,
                                draggable: true,
                                draggableCursor: 'default',
                                draggingCursor: 'pointer',
                                keyboardShortcuts: true,
                                disableDoubleClickZoom: false,
                                noClear: false,
                                styles: [
                                    {
                                        featureType: 'poi',
                                        elementType: 'labels',
                                        stylers: [
                                            {
                                                visibility: 'off',
                                            },
                                        ],
                                    },
                                ],
                            }}
                            mapContainerStyle={{ height: '100%', width: '100%' }}
                            zoom={zoom}
                            center={center}
                            onLoad={(map) => handleMapLoad(map)}
                            onZoomChanged={() => {
                                const newZoomLevel = mapRef.current?.getZoom();
                                if (
                                    newZoomLevel &&
                                    newZoomLevel !== zoom &&
                                    isMountedRef.current
                                ) {
                                    setZoom(newZoomLevel);
                                }
                            }}
                            onDragStart={()=>{setIsDragging(true)}}
                            onDragEnd={()=>{
                                setIsDragging(false)
                            }}
                            onCenterChanged={() => {
                                if (!isDragging && mapRef?.current) {
                                    const newCenter = mapRef.current?.getCenter();
                                    if (newCenter) {
                                        const newCenterObj = {
                                            lat: newCenter.lat(),
                                            lng: newCenter.lng(),
                                        };
                                        if (
                                            isMountedRef.current &&
                                            (newCenterObj.lat !== center.lat ||
                                                newCenterObj.lng !== center.lng)
                                        ) {
                                            setCenter(newCenterObj);
                                        }
                                    }
                                }
                            }}
                        >
                            {events?.map((event, i) => (
                                <Fragment key={`event_${i}`}>
                                    <MarkerF
                                        key={event.content.id}
                                        options={{
                                            icon: {
                                                url: markerConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                                scaledSize: new google.maps.Size(10, 10),
                                                scale: 1,
                                                origin: new google.maps.Point(0, 0),
                                            }
                                        }}
                                        onClick={(e) => {
                                            // Clear any pending close timer
                                            if (popupCloseTimeoutRef.current) {
                                                clearTimeout(popupCloseTimeoutRef.current);
                                                popupCloseTimeoutRef.current = null;
                                            }
                                            setIsHoveringPopup(true);
                                            setCenter({
                                                lat: +event?.latitude,
                                                lng: +event?.longitude,
                                            });
                                            setSelectedEventIdx(i);
                                        }}
                                        position={{
                                            lat: Number(event?.latitude),
                                            lng: Number(event?.longitude),
                                        }}
                                    />

                                    {(!event?.isPrecise && zoom >= 15) &&
                                        <Circle
                                            center={{
                                                lat: Number(event?.latitude),
                                                lng: Number(event?.longitude),
                                            }}
                                            radius={event?.appxDistMeters}
                                            options={{
                                                strokeColor: markerRadiusColorConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                                strokeWeight: 0.1,
                                                fillOpacity: 0.2,
                                                fillColor: markerRadiusColorConstants[getMarkerType(event,groupByKey,groupByEvents)!],
                                                center: {
                                                    lat: Number(event?.latitude),
                                                    lng: Number(event?.longitude),
                                                }
                                            }}
                                        />
                                    }
                                </Fragment>
                            ))}

                            {geofencesClone?.map((geofence, index) => {
                                const fillColor = geofence?.color;
                                const strokeColor = 'gray';

                                return (
                                    <Polygon
                                        key={index}
                                        editable={true}
                                        draggable={true}
                                        path={geofence.coordinates.map((coordinate) => ({ lat: coordinate.latitude, lng: coordinate.longitude }))}
                                        onMouseUp={(e) => onEdit(index, geofence.id)}
                                        onDragEnd={(e) => onEdit(index, geofence.id)}
                                        onLoad={(polygon) => handleLoad(polygon, index)}
                                        onUnmount={() => handleUnmount(index)}
                                        options={{
                                            fillColor: fillColor,
                                            strokeColor: strokeColor,
                                            fillOpacity: 0.4, // Adjust as needed
                                            strokeWeight: 0.4, // Adjust as needed
                                            geodesic: true,
                                        }}
                                    />
                                );
                            })}
                        </GoogleMap>
                    </When>

                    {/* Custom popup - positioned absolutely over the map */}
                    <CustomMapPopup
                        map={mapRef.current}
                        event={selectedEvent}
                        visible={selectedEvent !== null}
                        onClose={() => {
                            if (popupCloseTimeoutRef.current) {
                                clearTimeout(popupCloseTimeoutRef.current);
                            }
                            setSelectedEventIdx(null);
                            setIsHoveringPopup(false);
                        }}
                        onSeeMore={() => {
                            if (popupCloseTimeoutRef.current) {
                                clearTimeout(popupCloseTimeoutRef.current);
                            }
                            setIsHoveringPopup(false);
                            setEventModalVisible(true);
                            setZoom(ZOOM_LEVEL_EVENT);
                        }}
                        onMouseEnter={() => {
                            // User entered popup - cancel any pending close timer
                            if (popupCloseTimeoutRef.current) {
                                clearTimeout(popupCloseTimeoutRef.current);
                                popupCloseTimeoutRef.current = null;
                            }
                            setIsHoveringPopup(true);
                        }}
                        onMouseLeave={() => setIsHoveringPopup(false)}
                    />
                </Box>

            <Grid container mt={2} spacing={4}>
                <Grid item xs={12} md={12}>
                    <Grid flexDirection={'row'} mb={2} justifyContent={'space-between'} container>
                        <Typography fontSize={18} gutterBottom color={'text.default'} fontWeight={400}>
                            Active Geofences
                        </Typography>
                        <Button disabled={geofencesChanged} variant='contained' size='small' onClick={() => toggleModal('form', true)}>
                            Add new geofence
                        </Button>
                    </Grid>
                    <Card>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Geofence Name
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Phone Number
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Action
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {geofences?.map(
                                        (geofence, index) => (
                                            <>
                                                <TableRow
                                                    key={`geofence-${geofence.id}`}
                                                >
                                                    <TableCell sx={{
                                                        width: '50%',
                                                    }}>
                                                        <Stack flexDirection="row" alignItems={"center"}>
                                                            <Box
                                                                sx={{
                                                                    width: 16,
                                                                    height: 16,
                                                                    marginRight: 1,
                                                                    borderRadius: 100,
                                                                    backgroundColor: geofence?.color,
                                                                    filter: 'alpha(opacity=60)',
                                                                }}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                }}
                                                            >
                                                                {
                                                                    geofence?.description
                                                                }
                                                            </Typography>
                                                        </Stack>

                                                    </TableCell>
                                                    <TableCell sx={{
                                                        width: 500,
                                                    }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                            }}
                                                        >
                                                            {geofence?.phoneNumber}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1}>
                                                            <Tooltip title='Delete Geofence'>
                                                                <IconButton
                                                                    aria-label="delete_geofence"
                                                                    onClick={() => {
                                                                        setSelectedGeofence(geofence);
                                                                        toggleModal('delete', true);
                                                                    }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title='Edit Geofence'>
                                                                <IconButton
                                                                    aria-label="edit_geofence"
                                                                    onClick={() => {
                                                                        setSelectedGeofence(geofence);
                                                                        toggleModal('form', true);
                                                                    }}
                                                                >
                                                                    <Edit />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )
                                    )}
                                </TableBody>
                            </Table>

                            {!loading.fetchGeofences && geofences?.length === 0 &&
                                <NoData
                                    buttonText={'Add'}
                                    onClick={() => toggleModal('form', true)}
                                    title={'No geofences found'}
                                    description={
                                        'Apparently there is no geofence created in the system'
                                    }
                                />
                            }
                        </TableContainer>
                    </Card>
                </Grid>

                <Grid item xs={12} md={12}>
                    <Grid flexDirection={'row'} mb={2} justifyContent={'space-between'} container>
                        <Typography fontSize={18} gutterBottom color={'text.default'} fontWeight={400}>
                            Phone numbers
                        </Typography>
                        <When condition={!alreadyHaveUnknownLocation || !alreadyHaveOutsideLocation}>
                            <Button variant='contained' size='small' onClick={() => toggleModal('phoneForm', true)}>
                                Add new phone
                            </Button>
                        </When>
                    </Grid>

                    <Card>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Phone Label
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Phone Number
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Type
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                                Action
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {geoPhones?.map((phone) => (
                                        <TableRow
                                            key={`phone-${phone.id}`}
                                        >
                                            <TableCell sx={{ width: '40%' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {phone?.label}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: '30%' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {phone?.phoneNumber}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: '20%' }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    {geoPhoneTypeLabel[phone?.type]}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    <Tooltip title='Delete Phone'>
                                                        <IconButton
                                                            aria-label="delete_phone"
                                                            onClick={() => {
                                                                setSelectedPhone(phone);
                                                                toggleModal('deletePhone', true);
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title='Edit Phone'>
                                                        <IconButton
                                                            aria-label="edit_phone"
                                                            onClick={() => {
                                                                setSelectedPhone(phone);
                                                                toggleModal('phoneForm', true);
                                                            }}
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {!loading.fetchGeoPhones && geoPhones?.length === 0 &&
                                <NoData
                                    buttonText={'Add'}
                                    onClick={() => toggleModal('phoneForm', true)}
                                    title={'No phones found'}
                                    description={
                                        'Apparently there is no phone for geofence system created in the system'
                                    }
                                />
                            }
                        </TableContainer>
                    </Card>
                </Grid>
            </Grid>

            <AreYouSureModal
                title={`Update geofence ${geofenceToUpdate?.description}`}
                description='Do you want to confirm the update of that geofence area?'
                visible={modal.confirmEdit}
                onHide={() => {
                    toggleModal('confirmEdit', false);
                    setGeofenceToUpdate(null);
                }}
                onConfirm={() => {
                    toggleModal('confirmEdit', false);
                    GeofenceService.updateGeofence(geofenceToUpdate as GeofenceTypes.UpdateGeofenceParams, (isValid, msg) => { });
                }}
                loading={loading.updateGeofence}
            />

            <AreYouSureModal
                title={'Are you sure you want to delete this phone?'}
                description={'You will permanently lose this phone record.'}
                onHide={() => {
                    setSelectedPhone(null);
                    toggleModal('deletePhone', false);
                }}
                loading={loading?.deleteGeoPhone}
                onConfirm={async () => {
                    await GeofenceService.deleteGeoPhone(selectedPhone?.id!, (isValid, msg) => {
                        setSelectedPhone(null);
                        if (!isValid) {
                            toast(
                                msg,
                                {
                                    autoClose: 1500,
                                    type: 'error',
                                },
                            );
                        } else {
                            toast(
                                'Phone successfully deleted.',
                                {
                                    autoClose: 1500,
                                    type: 'success',
                                },
                            );
                        }

                        toggleModal('deletePhone', false);
                    });
                }}
                visible={modal.deletePhone}
            />

            <AreYouSureModal
                title={'Are you sure you want to delete this geofence?'}
                description={'You will permanently lose this geofence record.'}
                onHide={() => {
                    setSelectedGeofence(null);
                    toggleModal('delete', false);
                }}
                loading={loading?.deleteGeofence}
                onConfirm={async () => {
                    await GeofenceService.deleteGeofence(selectedGeofence?.id!, (isValid, msg) => {
                        setSelectedGeofence(null);
                        if (!isValid) {
                            toast(
                                msg,
                                {
                                    autoClose: 1500,
                                    type: 'error',
                                },
                            );
                        } else {
                            toast(
                                'Geofence successfully deleted.',
                                {
                                    autoClose: 1500,
                                    type: 'success',
                                },
                            );
                        }

                        toggleModal('delete', false);
                    });
                }}
                visible={modal.delete}
            />

            <When condition={selectedEvent?.type !== 'device' && selectedEvent?.type !== 'report' && eventModalVisible}>
                <AlertDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEventIdx(null);
                        }, 500);
                    }}
                    type={selectedEvent?.type}
                    visible={eventModalVisible}
                    alert={selectedEvent?.content as AlertTypes.Model}
                    zoomOut={() => setZoom(ZOOM_LEVEL_EVENT - 5)}
                />
            </When>

            <When condition={selectedEvent?.type === 'device'}>
                <DeviceDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEventIdx(null);
                        }, 500);
                    }}
                    visible={eventModalVisible}
                    device={selectedEvent?.content as DeviceTypes.Model}
                />
            </When>

            <When condition={selectedEvent?.type === 'report'}>
                <ReportDetailsDrawer
                    onClose={() => {
                        setEventModalVisible(false);
                        setTimeout(() => {
                            setSelectedEventIdx(null);
                        }, 500);
                    }}
                    visible={eventModalVisible}
                    report={selectedEvent?.content as ReportTypes.Model}
                />
            </When>

            <When condition={mapRef !== null}>
                <MapLegend isMultipleExists={containsMultipleEvents(groupByEvents)} isMultipleEvents={isMultipleEvents} />
            </When>
        </>
    );
}
